// Types and Interfaces
export interface ImageDimensions {
  width: number;
  height: number;
}

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

/**
 * Load image dimensions from file (returns { width, height } or null on error)
 */
const getImageDimensions = (file: File): Promise<ImageDimensions | null> =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    img.src = url;
  });

/**
 * Validates image file against specifications:
 * - File type: Must be an image
 * - File size: Maximum 1MB
 * - Aspect ratio: Must be 1:1 (square)
 *
 * @param file - The image file to validate
 * @returns Promise with validation result
 */
export const validateImageSpecs = async (file: File): Promise<ValidationResult> => {
  const errors: string[] = [];
  const MAX_FILESIZE = 1 * 1024 * 1024; // 1MB in bytes

  // Check file type
  if (!file.type.startsWith("image/")) {
    errors.push("File must be an image");
    return { isValid: false, errors };
  }

  // Check file size (max 1MB)
  if (file.size > MAX_FILESIZE) {
    errors.push(`Image file size must be less than or equal to 1MB. Current size: ${(file.size / (1024 * 1024)).toFixed(2)}MB`);
  }

  // Check aspect ratio 1:1 (square)
  const dimensions = await getImageDimensions(file);
  if (!dimensions) {
    errors.push("Could not read image dimensions. The file may be corrupted or in an unsupported format.");
  } else {
    const { width, height } = dimensions;
    if (width !== height) {
      errors.push(`Image aspect ratio must be 1:1 (square). Current dimensions: ${width}×${height}`);
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Get recommended image specs as a formatted string
 * @returns Formatted message string
 */
export const getImageSpecsMessage = (): string => {
  return "Maximum file size: 1MB. Aspect ratio must be 1:1 (square).";
};